// ═══════════════════════════════════════════════════════════════════════════
// Per-action draw-modify translators (CC-2c sub-arm 2) — PACKAGE-INTERNAL
// ═══════════════════════════════════════════════════════════════════════════
//
// The thin, total per-action translators behind the DRAW-MODIFY domain
// (TRIM/EXTEND/OFFSET/MIRROR/SCALE/STRETCH/ARRAYRECT/ARRAYEDIT). Each AI shape
// maps 1:1 (or 1:N for the array group) onto the corresponding contract
// `EditorCommand`, with no simplification — full required params per the
// EditorCommand + ТЗ.
//
// IMPORTANT (AI safe contour): these translators produce geometry
// `EditorCommand`s WITHOUT running the CC-2a fail-closed guard
// (`assertAiCallableDrawAction`). They are therefore PACKAGE-INTERNAL: this file
// is NOT re-exported by the barrel (`index.ts`). The ONLY public draw-modify
// entry is the guarded `translateDrawModifyCommand` in `drawModifyDispatcher.ts`
// (which imports these). This mirrors the BLOCK domain, whose per-action
// translators live in non-barrelled `blockDispatcher*.ts` sub-files that only
// the guarded `translateAICommandToDispatch` consumes — so a consumer of
// `@dxf-ai-editor/ai-commands` can never emit `geometry/trim-entity` etc. past
// the guard. Honours the CLAUDE.md rules:
//   "AI drafting … может только предлагать и исполнять команды через
//    безопасный контур." + "любые изменения геометрии только через Command
//    Engine."

import type { EditorCommand } from "@dxf-ai-editor/contracts";

import {
  AI_COMMAND_SOURCE,
  type AiDispatchContext,
} from "./blockDispatcherShared";
import type {
  ArrayEditAiCommand,
  ArrayRectAiCommand,
  ExtendAiCommand,
  MirrorAiCommand,
  OffsetAiCommand,
  ScaleAiCommand,
  StretchAiCommand,
  TrimAiCommand,
} from "./drawModifySchemas";

// ─── TRIM (FF-3) ───────────────────────────────────────────────────
export function translateTrim(command: TrimAiCommand): EditorCommand[] {
  return [
    {
      type: "geometry/trim-entity",
      source: AI_COMMAND_SOURCE,
      entityId: command.entityId,
      cuttingEdgeIds: command.cuttingEdgeIds,
      pickPoint: { x: command.pickPoint.x, y: command.pickPoint.y },
    },
  ];
}

// ─── EXTEND ────────────────────────────────────────────────────────
export function translateExtend(command: ExtendAiCommand): EditorCommand[] {
  return [
    {
      type: "geometry/extend-entity",
      source: AI_COMMAND_SOURCE,
      entityId: command.entityId,
      boundaryEdgeIds: command.boundaryEdgeIds,
      pickPoint: { x: command.pickPoint.x, y: command.pickPoint.y },
    },
  ];
}

// ─── OFFSET ────────────────────────────────────────────────────────
export function translateOffset(command: OffsetAiCommand): EditorCommand[] {
  return [
    {
      type: "geometry/offset-entity",
      source: AI_COMMAND_SOURCE,
      entityId: command.entityId,
      distance: command.distance,
      sidePoint: { x: command.sidePoint.x, y: command.sidePoint.y },
    },
  ];
}

// ─── MIRROR ────────────────────────────────────────────────────────
export function translateMirror(
  command: MirrorAiCommand,
  context: AiDispatchContext = {},
): EditorCommand[] {
  // MIRRTEXT precedence (Codex #342 F2 — AI safe contour "propose then
  // coerce"): an EXPLICIT proposal value wins; otherwise stamp the LIVE sysvar
  // threaded via `context.mirrtext` (mirrors `useDraftSession` reading
  // `useSysvarStore`); the final fallback is the AutoCAD default 0. This is the
  // ONLY draw-modify translator that consumes `context` — the others are fully
  // self-contained.
  const mirrtext = command.mirrtext ?? context.mirrtext ?? 0;
  return [
    {
      type: "geometry/mirror-entities",
      source: AI_COMMAND_SOURCE,
      entityIds: command.entityIds,
      mirrorLine1: { x: command.mirrorLine1.x, y: command.mirrorLine1.y },
      mirrorLine2: { x: command.mirrorLine2.x, y: command.mirrorLine2.y },
      deleteSource: command.deleteSource,
      mirrtext,
    },
  ];
}

// ─── SCALE ─────────────────────────────────────────────────────────
export function translateScale(command: ScaleAiCommand): EditorCommand[] {
  return [
    {
      type: "geometry/scale-entities",
      source: AI_COMMAND_SOURCE,
      entityIds: command.entityIds,
      basePoint: { x: command.basePoint.x, y: command.basePoint.y },
      factor: command.factor,
    },
  ];
}

// ─── STRETCH ───────────────────────────────────────────────────────
export function translateStretch(command: StretchAiCommand): EditorCommand[] {
  return [
    {
      type: "geometry/stretch-entities",
      source: AI_COMMAND_SOURCE,
      entityIds: command.entityIds,
      stretchWindow: {
        x1: command.stretchWindow.x1,
        y1: command.stretchWindow.y1,
        x2: command.stretchWindow.x2,
        y2: command.stretchWindow.y2,
      },
      delta: { x: command.delta.x, y: command.delta.y },
    },
  ];
}

// ─── ARRAYRECT ─────────────────────────────────────────────────────
//
// `arrayGroupId` is generated by the dispatch layer (contract: "Generated by
// caller … keeps reducer deterministic"). The AI normally omits it; we mint
// `arr-<uuid>` — the SAME format the draft state machine uses
// (draftModifyStateMachine.ts) — unless the AI supplied one (so a later
// ARRAYEDIT in the same turn can reference a known group). `axisAngle` flows
// through in DEGREES (the reducer converts to radians).
export function translateArrayRect(
  command: ArrayRectAiCommand,
): EditorCommand[] {
  const arrayGroupId =
    command.arrayGroupId ?? `arr-${globalThis.crypto.randomUUID()}`;
  return [
    {
      type: "geometry/create-arrayrect",
      source: AI_COMMAND_SOURCE,
      entityIds: command.entityIds,
      arrayGroupId,
      basePoint: { x: command.basePoint.x, y: command.basePoint.y },
      rowCount: command.rowCount,
      columnCount: command.columnCount,
      rowSpacing: command.rowSpacing,
      columnSpacing: command.columnSpacing,
      axisAngle: command.axisAngle,
    },
  ];
}

// ─── ARRAYEDIT ─────────────────────────────────────────────────────
//
// Re-parameterise an existing associative array group. Exactly the params
// block matching `mode` is carried through (the schema refine guarantees it is
// present). Polar/path edits are preserved at full fidelity (editing a
// human-authored polar/path array is legitimate even though AI CREATION of
// those is deferred per ТЗ §10.2).
export function translateArrayEdit(
  command: ArrayEditAiCommand,
): EditorCommand[] {
  return [
    {
      type: "geometry/arrayedit",
      source: AI_COMMAND_SOURCE,
      arrayGroupId: command.arrayGroupId,
      mode: command.mode,
      ...(command.rect !== undefined
        ? {
            rect: {
              rowCount: command.rect.rowCount,
              columnCount: command.rect.columnCount,
              rowSpacing: command.rect.rowSpacing,
              columnSpacing: command.rect.columnSpacing,
              axisAngle: command.rect.axisAngle,
            },
          }
        : {}),
      ...(command.polar !== undefined
        ? {
            polar: {
              centerPoint: {
                x: command.polar.centerPoint.x,
                y: command.polar.centerPoint.y,
              },
              itemCount: command.polar.itemCount,
              fillAngle: command.polar.fillAngle,
              rotateItems: command.polar.rotateItems,
              rowCount: command.polar.rowCount,
              rowSpacing: command.polar.rowSpacing,
            },
          }
        : {}),
      ...(command.path !== undefined
        ? {
            path: {
              method: command.path.method,
              ...(command.path.itemCount !== undefined
                ? { itemCount: command.path.itemCount }
                : {}),
              ...(command.path.itemSpacing !== undefined
                ? { itemSpacing: command.path.itemSpacing }
                : {}),
              alignItems: command.path.alignItems,
              pathEntityId: command.path.pathEntityId,
            },
          }
        : {}),
    },
  ];
}
